import { getPhrase, Say } from "../../languages";
import { RenderOptions } from "../../types";
import BackupCta from "./BackupCta";
import ChosenApp from "./ChosenApp";
import "../../assets/css/chosen/layout.css";
import ScalableImage from "../Shared/ScalableImage";
import { triggerAnalyticsEvent } from "../../helpers";

export default function ChosenAppLayout(props) {
  const renderOptions: RenderOptions = window.wvusFlexCheckoutSettings.renderOptions || {};

  const {
    featured_image: featuredImage,
    partner_logo: partnerLogo,
    is_order_thankyou: isOrderThankyou = false,
    layout_mode: layoutMode = "",
    backup_cta_url: backupCtaUrl,
    crm_event_status: crmEventStatus = "",
    event_start_date: eventStartDate = "",
    show_disclaimer: showDisclaimer = "",
    custom_disclaimer_text: customDisclaimerText = ""
  } = renderOptions;

  if (layoutMode !== "normal") {
    const modeMapping = {
      ended: {
        errorMessage: `${getPhrase("event_ended.heading")}\n\n${getPhrase("event_ended.sub_heading")}`,
        eventAction: "chosen_event_ended"
      },
      notstarted: {
        errorMessage: getPhrase("event_not_started.paragraph"),
        eventAction: "chosen_event_not_started"
      },
      error: {
        errorMessage: getPhrase("errors.general_error_text"),
        eventAction: "chosen_event_not_found"
      },
      nocb: {
        errorMessage: getPhrase("errors.general_error_text"),
        eventAction: "chosen_event_not_found"
      }
    };

    const { errorMessage = "", eventAction = "" } = modeMapping[layoutMode] || {};

    triggerAnalyticsEvent("error_view", {
      event_action: eventAction,
      event_label: errorMessage,
      cobrand: window.wvusCobrand || "",
      keyword: window.wvusChosenEventData?.[0]?.SmsKeyword,
      error_type: "chosen_photo",
      error_message: errorMessage,
      event_category: "chosen_error"
    });
  }

  return (
    <div
      id="primary"
      className={`page-layout-chosen content-area content-area__${layoutMode} ${
        isOrderThankyou ? "thankyou__layout" : ""
      }`}>
      <main id="main" className="site-main" role="main">
        {/* Featured Partner Section */}
        <div className="featured-partner">
          <div className="featured-partner__image">
            {featuredImage && (
              <ScalableImage
                src={featuredImage.url}
                alt={featuredImage.alt}
                width={featuredImage.width}
                height={featuredImage.height}
              />
            )}
          </div>
          <div className="featured-partner__info">
            <div className="featured-partner__partner-logo">
              {partnerLogo && (
                <img
                  src={partnerLogo.url}
                  alt={partnerLogo.alt}
                  width={partnerLogo.width}
                  height={partnerLogo.height}
                />
              )}
            </div>
            <h1 className="featured-partner__tagline">
              {isOrderThankyou ? (
                <Say phrase="hero_section.thank_you_message" />
              ) : (
                <Say phrase="hero_section.welcome_message" renderHtml={true} />
              )}
            </h1>
          </div>
        </div>

        {/* Spinner Loader */}
        {crmEventStatus === "ActiveEvent" && (
          <div className="wvus-streamlined-loader spinner--wv-star">
            <div className="double-bounce1"></div>
            <div className="double-bounce2"></div>
          </div>
        )}

        {/* Error Wrapper */}
        <div className="error-wrapper">
          {["error", "nocb"].includes(layoutMode) && (
            <>
              <div className="error-wrapper__header">
                <h2 className="delta">
                  <Say phrase="errors.error_heading" />
                </h2>
              </div>
              <div className="error-icon">
                <Say phrase="errors.error_icon_alt_text">
                  <img
                    src="https://wvusstatic.com/chosen/chosen-starter-restart.svg"
                    width="125"
                    height="125"
                    alt={getPhrase("errors.error_icon_alt_text")}
                  />
                </Say>
              </div>
              <p className="epsilon" id="error_message_text">
                <Say phrase="errors.general_error_text" />
              </p>
              <BackupCta url={backupCtaUrl} />
            </>
          )}

          {layoutMode === "notstarted" && (
            <>
              <div className="error-wrapper__header">
                <h2>
                  <Say phrase="event_not_started.heading" />
                </h2>
                <p id="error_message_text">
                  <Say phrase="event_not_started.paragraph" />
                </p>
              </div>
              <div className="error-icon">
                <Say phrase="event_not_started.begins_icon_alt_text">
                  <img
                    src="https://wvusstatic.com/chosen/chosen-event-calendar.svg"
                    width="125"
                    height="125"
                    alt={getPhrase("event_not_started.begins_icon_alt_text")}
                  />
                </Say>
              </div>
              <p>
                <Say phrase="event_not_started.begins_prefix" />
              </p>
              <div className="grey__bar">
                <p className="event-date">{eventStartDate}</p>
              </div>
              <p>
                <a href="/sponsor-a-child/chosen">
                  <Say phrase="event_not_started.learn_more_link" renderHtml={true} />
                </a>
              </p>
            </>
          )}

          {layoutMode === "ended" && (
            <>
              <div className="error-icon">
                <img
                  src="https://wvusstatic.com/chosen/chosen-starter-ended.svg"
                  width="125"
                  height="125"
                  alt={getPhrase("errors.error_icon_alt_text")}
                />
              </div>
              <div className="error-wrapper__header" id="error_message_text">
                <h2>
                  <Say phrase="event_ended.heading" />
                </h2>
                <p>
                  <Say phrase="event_ended.sub_heading" />
                </p>
              </div>
              <div className="grey__bar">
                <p>
                  <Say phrase="event_ended.paragraph" />
                </p>
              </div>
              <BackupCta url={backupCtaUrl} />
            </>
          )}
        </div>

        {/* Checkout App */}
        {layoutMode === "normal" && <ChosenApp {...props} />}

        {/* Disclaimer Text */}
        {showDisclaimer === "Yes" && (
          <p className="disclaimer__text centi">
            <Say
              phrase="chosen.default_paid_disclaimer"
              renderHtml={true}
              dynamicValue={customDisclaimerText}
            />
          </p>
        )}
      </main>
    </div>
  );
}
